import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import api from '../utils/Api';
import { playerCodeFromPath, trackEvent } from '../utils/Analytics';
import { formatPrice } from './BuyBumps';

import './JoinPlus.scss';

export interface PlusStatus {
  isPlus: boolean;
  plusUntil: string | null;
  canManage: boolean;
  offer: { name: string; amount: number; currency: string; interval: string } | null;
}

interface JoinPlusProps {
  token: string;
  // The page Stripe sends them back to, such as /jukebox/abc123.
  returnPath: string;
  onNotice: (tone: 'ok' | 'bad', text: string) => void;
  // Called while the membership is being switched on, so the screen can re-read the account.
  onJoined?: () => void;
}

/**
 * Jookable+ on the jukebox screen: the pitch and the Join button for everyone else, and a
 * short "you're a member" line with a way to manage it for members. Stripe hosts the
 * payment and the manage pages. The membership is switched on by the server once Stripe
 * confirms the payment, never by this page, so coming back here only re-reads it.
 *
 * Shows nothing at all when the server has no Stripe keys.
 */
const JoinPlus = ({ token, returnPath, onNotice, onJoined }: JoinPlusProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<PlusStatus>(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    api
      .users('plus', 'GET', token, null)
      .then((response: PlusStatus) => {
        setStatus(response);
        return response;
      })
      .catch(() => null);

  useEffect(() => {
    load();
  }, [token]);

  // Back from Stripe. The webhook can land a moment after they do, so the membership is
  // re-read for a little while. The flag comes off the address so a reload does not repeat it.
  useEffect(() => {
    const outcome = new URLSearchParams(location.search).get('plus');
    if (outcome !== 'joined' && outcome !== 'cancelled') {
      return;
    }
    navigate(location.pathname, { replace: true });
    trackEvent(outcome === 'joined' ? 'plus_joined' : 'plus_checkout_cancelled', { player_code: playerCodeFromPath(location.pathname) });
    if (outcome === 'cancelled') {
      onNotice('bad', 'Jookable+ sign-up cancelled. Nothing was charged.');
      return;
    }
    onNotice('ok', 'Welcome to Jookable+! Your songs now play sooner.');
    let tries = 0;
    const poll = window.setInterval(() => {
      tries += 1;
      load().then(response => {
        if ((response && response.isPlus) || tries >= 8) {
          window.clearInterval(poll);
          if (onJoined) onJoined();
        }
      });
    }, 2500);
    return () => window.clearInterval(poll);
  }, [location.search]);

  if (!status || (!status.offer && !status.isPlus)) {
    return null;
  }

  const go = async (path: 'plus/join' | 'plus/manage') => {
    setBusy(true);
    try {
      const { url } = await api.users(path, 'POST', token, { returnPath });
      trackEvent(path === 'plus/join' ? 'plus_checkout_started' : 'plus_manage_opened', {
        player_code: playerCodeFromPath(location.pathname),
      });
      window.location.assign(url);
    } catch (error) {
      setBusy(false);
      onNotice('bad', error.message);
    }
  };

  if (status.isPlus) {
    return (
      <div className="join-plus is-member">
        <span className="join-plus-member">
          <span className="join-plus-mark">Jookable+</span> member. Your songs play ahead of free songs, and your bumps outrank everyone else's.
        </span>
        {status.canManage && (
          <button type="button" className="join-plus-manage" disabled={busy} onClick={() => go('plus/manage')}>
            {busy ? 'Opening' : 'Manage'}
          </button>
        )}
      </div>
    );
  }

  const { offer } = status;
  return (
    <div className="join-plus">
      <div className="join-plus-title">
        Hear your songs sooner with <span className="join-plus-mark">Jookable+</span>
      </div>
      <ul className="join-plus-perks">
        <li>Every song you add jumps ahead of free songs in the queue, at every Jookable venue.</li>
        <li>No bumps needed. It works on every song, every night.</li>
        <li>Free users can't bump past your songs. Only a super bump can pass a Jookable+ member.</li>
        <li>A Jookable+ ring on your profile picture, so the whole room knows.</li>
        <li>Your bumps and super bumps outrank everyone else's. A Jookable+ bump can pass a super bumped song, and a Jookable+ super bump plays before other super bumps.</li>
      </ul>
      <button type="button" className="join-plus-join" disabled={busy} onClick={() => go('plus/join')}>
        {busy ? 'Opening' : `Join for ${formatPrice(offer.amount, offer.currency)} a ${offer.interval}`}
      </button>
      <div className="join-plus-small">Cancel anytime. Payment is handled by Stripe.</div>
    </div>
  );
};

export default JoinPlus;
